/**
 * Frontend Logging Utility
 * Simple structured logging for client-side (browser console)
 
 * Client logs = Browser console (this file)
 * Server logs = Python logging module (FastAPI backend)
 * 
 * ERROR logs are also sent to backend for production monitoring
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8001";

/**
 * Log levels
 */
const LogLevel = {
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR',
};

/**
 * Structured logger
 */
class Logger {
  constructor(context = 'App') {
    this.context = context;
  }

  /**
   * Format log message with timestamp
   * Similar to Python's logging format
   */
  formatMessage(level, message) {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level}] [CLIENT] ${message}`;
  }

  /**
   * Send error to backend (non-blocking, fire and forget)
   * Only called for ERROR level logs - for production monitoring.
   * Skipped when REACT_APP_ENV=development so UI works without backend.
   */
  sendErrorToBackend(message, error = null) {
    if (process.env.REACT_APP_ENV === 'development') return;
    try {
      const errorData = {
        timestamp: new Date().toISOString(),
        level: 'ERROR',
        context: this.context,
        message: message,
        error: error ? {
          message: error.message,
          stack: error.stack,
          name: error.name,
          ...(error.response && {
            response: {
              status: error.response.status,
              data: error.response.data,
            },
          }),
        } : null,
        browser: {
          userAgent: navigator.userAgent,
          url: window.location.href,
        },
      };

      // Use fetch with keepalive for non-blocking, reliable delivery
      // This works even if the page is unloading (similar to sendBeacon)
      const logData = JSON.stringify(errorData);
      
      fetch(`${API_BASE_URL}/api/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: logData,
        keepalive: true, // Ensures request completes even if page unloads
      }).catch(() => {
        // Silently fail - logging should never break the app
      });
    } catch (err) {
      // Silently fail - don't break the app if logging fails
    }
  }

  /**
   * Debug logs (only in development)
   * Similar to Python: logger.debug()
   */
  debug(message, data = null) {
    if (isDevelopment) {
      const formatted = this.formatMessage(LogLevel.DEBUG, message);
      if (data) {
        console.debug(formatted, data);
      } else {
        console.debug(formatted);
      }
    }
  }

  /**
   * Info logs
   */
  info(message, data = null) {
    const formatted = this.formatMessage(LogLevel.INFO, message);
    if (data) {
      console.info(formatted, data);
    } else {
      console.info(formatted);
    }
  }

  /**
   * Warning logs
   */
  warn(message, error = null) {
    const formatted = this.formatMessage(LogLevel.WARN, message);
    if (error) {
      console.warn(formatted, error);
    } else {
      console.warn(formatted);
    }
  }

  /**
   * Error logs
   * Also sends to backend for production monitoring
   */
  error(message, error = null) {
    const formatted = this.formatMessage(LogLevel.ERROR, message);
    if (error) {
      console.error(formatted, error);
    } else {
      console.error(formatted);
    }
    
    // Send to backend (non-blocking) - for production monitoring
    // You'll see these in your server console with [CLIENT ERROR] tag
    this.sendErrorToBackend(message, error);
  }

  /**
   * Create a logger instance for a specific context
   */
  static create(context) {
    return new Logger(context);
  }
}

// Default logger instance
const logger = new Logger();

export default logger;
export { Logger, LogLevel };

